/**
 * Settings control for server-sent push notifications on this device: turn push on (permission,
 * subscribe with the server's VAPID key, upload), turn it off, see the current status, and send a
 * test. On iOS outside the installed Home Screen app, explains that installation is required.
 *
 * @module apps/web/src/components/notifications/PushNotificationSettings
 */

import { useEffect, useState } from 'react';
import { BellRing, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  disablePush,
  enablePush,
  isIos,
  isStandalone,
  notificationsApi,
  pushStatus,
  type PushStatus,
} from '@/lib/notificationsApi';

/** Status line shown for each push state. */
const STATUS_TEXT: Record<PushStatus, string> = {
  unsupported: 'This browser does not support push notifications.',
  'needs-install': 'Add to Home Screen to get notifications.',
  'not-configured': 'Push notifications are not available on this server yet.',
  denied: 'Notifications are blocked for Fluxability in this browser.',
  off: 'Off on this device.',
  on: 'On for this device.',
};

/** Push toggle, status, test button and iOS install hint. */
export function PushNotificationSettings() {
  const { toast } = useToast();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const showIosHint = isIos() && !isStandalone();

  useEffect(() => {
    let active = true;
    pushStatus()
      .then(next => active && setStatus(next))
      .catch(() => active && setStatus('unsupported'));
    return () => {
      active = false;
    };
  }, []);

  const turnOn = async () => {
    setBusy(true);
    try {
      const result = await enablePush();
      setStatus(result.status);
      if (result.message) toast({ title: 'Push notifications', description: result.message, variant: 'destructive' });
      else toast({ title: 'Push notifications on', description: 'Fluxability can ask you a quick energy question once a day.' });
    } catch (error) {
      toast({
        title: 'Push notifications',
        description: error instanceof Error ? error.message : 'Could not turn on push notifications.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
      setStatus('off');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const response = await notificationsApi.sendTest();
      toast(
        response.success
          ? {
              title: 'Test sent',
              description: response.data?.delivered
                ? 'Check this device for the notification.'
                : 'The push service did not accept it. Try turning push off and on.',
            }
          : { title: 'Test not sent', description: response.error?.message || 'Try again shortly.', variant: 'destructive' }
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border bg-background px-3 py-3" data-testid="push-settings">
      <div className="flex flex-wrap items-center gap-2">
        <BellRing className="h-4 w-4" />
        <span className="text-sm font-medium">Enable push notifications</span>
        <span className="text-xs text-muted-foreground" data-testid="push-status">
          {status ? STATUS_TEXT[status] : 'Checking…'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        At most one short energy question a day, between 09:00 and 20:00 your time. Tapping it opens Flux Agent in voice mode.
      </p>
      {showIosHint && status !== 'needs-install' && (
        <p className="text-xs text-muted-foreground">On iPhone and iPad, Add to Home Screen to get notifications.</p>
      )}
      <div className="flex flex-wrap gap-2">
        {status === 'on' ? (
          <>
            <Button variant="outline" size="sm" disabled={busy} onClick={turnOff}>
              Turn off on this device
            </Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={sendTest}>
              <Send className="mr-2 h-4 w-4" />
              Send test
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy || status === null || status === 'unsupported' || status === 'needs-install' || status === 'denied'}
            onClick={turnOn}
          >
            Enable push notifications
          </Button>
        )}
      </div>
    </div>
  );
}
